import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { useLogin } from "@/hooks/useAuth";
import { useForm } from "react-hook-form";
import { loginSchema, type LoginFormData } from "@/schemas/authSchemas";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { useLocation } from "@/contexts/LocationContext";
import { saveUserData } from "@/utils/jwt";
import {
  LogIn,
  Mail,
  Lock,
  Shield,
  Users,
  MapPin,
  Eye,
  EyeOff,
  Megaphone,
} from "lucide-react";
import { motion } from "framer-motion";
import PrefLogo from "@/assets/logo_municipal.svg";

export const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const login = useLogin();
  const navigate = useNavigate();
  const { requestLocation } = useLocation();

  const {
    handleSubmit,
    register: registerField,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    await login.mutateAsync(
      {
        email: data.email,
        password: data.password,
      },
      {
        onSuccess: async (response) => {
          // Salvar token e dados do usuário
          localStorage.setItem("duas-vidas-token", response.access_token);
          saveUserData(response.user);

          toast.success("Login realizado com sucesso!");

          // Redirecionar baseado na role do usuário
          if (response.user.role === "AGENT") {
            // Agentes não precisam de localização imediata
            navigate("/ocurrences", { replace: true });
          } else {
            // Solicitar permissão de localização para cidadãos
            await requestLocation();
            navigate("/home", { replace: true });
          }
        },
        onError: () => {
          toast.error("Erro ao realizar login. Verifique suas credenciais.");
        },
      },
    );
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-[hsl(var(--primary))]/20 via-[hsl(var(--primary))]/5 to-[hsl(var(--secondary))]/10">
      {/* Elementos decorativos de fundo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Círculos decorativos */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute -top-24 -left-24 w-96 h-96 bg-[hsl(var(--primary))]/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, -90, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute -bottom-24 -right-24 w-96 h-96 bg-[hsl(var(--secondary))]/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            y: [0, -20, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute top-1/2 left-1/4 w-64 h-64 bg-[hsl(var(--primary))]/5 rounded-full blur-2xl"
        />

        {/* Grid de pontos decorativo */}
        <div className="absolute inset-0 opacity-20">
          <div className="h-full w-full bg-[radial-gradient(#10b981_1px,transparent_1px)] [background-size:20px_20px]" />
        </div>
      </div>

      {/* Container principal com duas colunas */}
      <div className="w-full max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center relative z-10">
        {/* Coluna esquerda - Informações */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="hidden md:block"
        >
          <div className="space-y-8">
            <div>
              <div className="mb-2">
                <p className="text-sm font-semibold text-[hsl(var(--primary))] uppercase tracking-wider mb-2">
                  
                </p>
                <h1 className="text-6xl font-black mb-2 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] bg-clip-text text-transparent">
                  Vida+Simples
                </h1>
                <p className="text-lg font-medium text-gray-700">Ceará</p>
              </div>
              <p className="text-xl text-gray-600 leading-relaxed mt-6">
                Plataforma inteligente de gestão urbana e atendimento ao cidadão
              </p>
            </div>

            {/* Cards de features */}
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-[hsl(var(--primary))]/20"
              >
                <div className="p-3 bg-[hsl(var(--primary))]/10 rounded-lg">
                  <Megaphone className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Reporte Problemas
                  </h3>
                  <p className="text-sm text-gray-600">
                    Denuncie problemas urbanos e acompanhe o andamento das
                    soluções
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-[hsl(var(--primary))]/20"
              >
                <div className="p-3 bg-[hsl(var(--primary))]/10 rounded-lg">
                  <MapPin className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Localização em Tempo Real
                  </h3>
                  <p className="text-sm text-gray-600">
                    Veja ocorrências próximas a você e em toda a cidade
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-[hsl(var(--primary))]/20"
              >
                <div className="p-3 bg-[hsl(var(--primary))]/10 rounded-lg">
                  <Shield className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Transparência Total
                  </h3>
                  <p className="text-sm text-gray-600">
                    Acompanhe cada etapa da resolução com total transparência
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Coluna direita - Formulário de Login */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-full"
        >
          <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
            <CardHeader className="space-y-4 pb-8">
              <div className="flex items-center justify-center mb-2">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                >
                  <img
                    src={PrefLogo}
                    alt="Logo Municipal"
                    className="h-25 w-25"
                  />
                </motion.div>
              </div>
              <CardTitle className="text-3xl font-bold text-center text-gray-900">
                Bem-vindo de volta
              </CardTitle>
              <p className="text-center text-muted-foreground text-sm">
                Faça login para acessar sua conta
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      className={`pl-5 pr-4 focus-visible:ring-[hsl(var(--primary))] ${errors.email ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                      {...registerField("email")}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-sm text-red-500">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Senha */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">
                    Senha
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className={`pl-5 pr-12 focus-visible:ring-[hsl(var(--primary))] ${errors.password ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                      {...registerField("password")}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-sm text-red-500">
                      {errors.password.message}
                    </p>
                  )}
                </div>

                {/* Link Esqueceu a senha */}
                <div className="flex justify-end">
                  <Link
                    to="/recovery-password"
                    className="text-sm text-[hsl(var(--primary))] hover:underline font-medium"
                  >
                    Esqueceu a senha?
                  </Link>
                </div>

                {/* Botão de Login */}
                <Button
                  type="submit"
                  disabled={login.isPending}
                  className="w-full text-white cursor-pointer h-12 text-base font-medium bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] hover:from-[hsl(var(--primary))]/90 hover:to-[hsl(var(--secondary))]/90 shadow-lg"
                >
                  {login.isPending ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Entrando...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <LogIn className="h-5 w-5" />
                      Entrar no Sistema
                    </div>
                  )}
                </Button>
              </form>

              {/* Rodapé do card */}
              <div className="mt-6 pt-6 border-t text-center">
                <p className="text-sm text-muted-foreground">
                  Não tem uma conta?{" "}
                  <Link
                    to="/register"
                    className="text-[hsl(var(--primary))] font-semibold hover:underline"
                  >
                    Cadastre-se
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Footer mobile */}
          <p className="text-center text-sm text-gray-600 mt-8 md:hidden">
            © 2026 Vida Mais Simples - Frecheirinha, CE
          </p>
        </motion.div>
      </div>

      {/* Footer desktop */}
      <div className="absolute bottom-6 left-0 right-0 text-center hidden md:block">
        <p className="text-sm text-gray-600">
          © 2026 Vida Mais Simples - Frecheirinha, CE
        </p>
      </div>
    </div>
  );
};
